import { useLayoutEffect, useRef } from 'react';

import { HeaderUtilityCluster } from './HeaderUtilityCluster';

/**
 * Persistent top-right utilities, overlaid on the page header row so title +
 * subtitle stay on the same line as before. Mounted outside AnimatedOutlet so
 * search / weather / share / AI / notifications / avatar never remount on
 * navigation. PageHeader reserves matching right padding so content doesn't
 * run under this cluster.
 *
 * The cluster measures itself and publishes that box as `--chrome-utilities-width`
 * (plus a small gap) and `--chrome-utilities-height`. A header reserving space through
 * `utilityOverlay` therefore tracks the cluster's real size; PageHeader's `utilityPad`
 * still uses the per-breakpoint estimates.
 */
export function AppChromeHeader() {
  const clusterRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = clusterRef.current;
    if (!el) return;

    const publish = () => {
      const box = el.getBoundingClientRect();
      const root = document.documentElement.style;
      root.setProperty('--chrome-utilities-width', `${Math.ceil(box.width) + 8}px`);
      root.setProperty('--chrome-utilities-height', `${Math.ceil(box.height)}px`);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={clusterRef}
      className="pointer-events-none absolute top-0 right-0 z-30 px-2 sm:px-4 md:px-6 py-2"
    >
      <div className="pointer-events-auto">
        <HeaderUtilityCluster />
      </div>
    </div>
  );
}
